/**
 * Bringing a document up to the model this build knows.
 *
 * A document stored as JSON of an in-memory type is a promise that the type
 * will never change, and that is a promise no program keeps. The way out is
 * not to stop changing the model — it is to say which version each document
 * was written against and to keep, forever, the steps from each version to the
 * next.
 *
 * The rules this file exists to enforce:
 *
 *   · A document with no version is version 0. Every row written before the
 *     field existed is one, so the migration from 0 is the one that had to work
 *     before any of this was worth having.
 *   · Migrations run in order, each taking a document from n to n+1. Never a
 *     jump: a chain of small steps can be reasoned about one at a time, and
 *     each one only has to know about the version before it.
 *   · A version *newer* than this build knows is refused outright, with the
 *     numbers in the message. Half-reading a file from a later build is the one
 *     failure that loses work rather than merely reporting it — the document
 *     opens, looks close enough to right, and is saved back over the good copy
 *     with whatever this build did not understand quietly dropped.
 *   · A migration reports what it changed. Silence about a repair is how the
 *     duplicate-id defect went unnoticed for as long as it did.
 *
 * Adding a version: raise {@link FORMAT_VERSION} in `model.ts`, add one entry
 * here, and add a fixture at the version before it under
 * `tools/fixtures/native/`. `tools/check-migrate.ts` refuses a version with no
 * migration, a migration with no fixture, and a fixture that does not come out
 * current.
 */

import { FORMAT_VERSION, viaType, type CadDocument, type Entity } from '../model.ts';
import { ensureUniqueIds, seedIdsFrom } from '../geom/transform.ts';

/** One step, from `to - 1` to `to`. */
export interface Migration {
  /** The version this step produces. */
  to: number;
  /** What changed about the model, in a line. Shown when a document moves. */
  note: string;
  /** Apply in place. Returns how many things it had to put right. */
  apply(doc: CadDocument): number;
}

/**
 * Every step, in order. Append; never renumber, never edit one that has
 * shipped — a document somewhere has been through it and expects it to have
 * meant what it meant.
 */
export const MIGRATIONS: Migration[] = [
  {
    to: 1,
    note: 'entity ids made unique',
    // Connectivity is keyed by id, so two entities sharing one are the same
    // object as far as the program is concerned: two pads fuse, their nets
    // merge, and the ratsnest draws nothing across a join it thinks is already
    // made. Documents written before component instantiation reassigned child
    // ids carry exactly that, and it looks like a rendering fault rather than
    // like bad data.
    apply: (doc) => ensureUniqueIds(doc.entities).repaired,
  },
  {
    to: 2,
    note: 'auto-routed vias made of copper',
    /**
     * Every via the router placed was stamped `T0` — the schematic pin type,
     * which `padIsSchematic` excludes from copper everywhere. On a board that
     * is a via that connectivity does not follow, the pour does not clear, the
     * clearance check does not measure, Gerber does not plot and Excellon does
     * not drill. The route reads as made in the router's own log and as an
     * open everywhere else, and the fabricated board has two unjoined tracks
     * where it should have a hole.
     *
     * Narrow on purpose. Only a board, only a pad the router made (`auto`),
     * only one with a drill in it — that is a via and can be nothing else. A
     * schematic dot the user placed is untouched, and so is a `T0` on a
     * schematic, where it is exactly right.
     */
    apply: (doc) => {
      if (doc.kind !== 'pcb') return 0;
      const want = viaType(doc);
      let changed = 0;
      const walk = (list: Entity[]): void => {
        for (const e of list) {
          if (e.kind === 'component') { walk(e.children); continue; }
          if (e.kind !== 'pad') continue;
          if (e.type !== 'T0' || e.auto !== true) continue;
          if (!e.hole || e.hole <= 0) continue;
          e.type = want;
          e.through = true;
          changed++;
        }
      };
      walk(doc.entities);
      return changed;
    },
  },
];

/** Raised when a document was written by a build newer than this one. */
export class FutureVersionError extends Error {
  readonly found: number;
  readonly known: number;

  constructor(found: number, known: number) {
    super(`This drawing was saved by a newer version of SirCAD `
      + `(format ${found}; this build reads up to ${known}). `
      + `Update before opening it — opening it here would drop whatever is new.`);
    this.name = 'FutureVersionError';
    this.found = found;
    this.known = known;
  }
}

/**
 * What version a document claims to be.
 *
 * Anything that is not a whole number at or above zero is treated as 0 rather
 * than as an error: a document with a corrupt version field is an old document
 * as far as we can tell, and the migrations are written to be safe on a
 * document that has already had them.
 */
export function documentVersion(doc: unknown): number {
  const v = (doc as { formatVersion?: unknown } | null)?.formatVersion;
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 ? v : 0;
}

export interface MigrationStep {
  to: number;
  note: string;
  /** How many things this step had to put right. */
  changed: number;
}

export interface MigrationResult {
  document: CadDocument;
  from: number;
  to: number;
  /** The steps that ran, in order. Empty when the document was already current. */
  steps: MigrationStep[];
  /** Everything the steps put right, added up. */
  changed: number;
}

/**
 * Bring a document up to date, in place.
 *
 * Always seeds the id counter, whether or not anything migrated: that is a fact
 * about this session rather than about the document, and every load needs it.
 *
 * Throws {@link FutureVersionError} for a document from a later build. Callers
 * are expected to catch it and say so — a project of five sheets should open
 * the four it can read and name the one it cannot.
 */
export function migrateDocument(doc: CadDocument): MigrationResult {
  const from = documentVersion(doc);
  if (from > FORMAT_VERSION) throw new FutureVersionError(from, FORMAT_VERSION);

  seedIdsFrom(doc.entities);

  const steps: MigrationStep[] = [];
  for (const m of MIGRATIONS) {
    if (m.to <= from) continue;
    steps.push({ to: m.to, note: m.note, changed: m.apply(doc) });
  }

  doc.formatVersion = FORMAT_VERSION;
  return {
    document: doc,
    from,
    to: FORMAT_VERSION,
    steps,
    changed: steps.reduce((n, s) => n + s.changed, 0),
  };
}

/** A line for the status bar, or null when there was nothing to say. */
export function migrationNote(r: MigrationResult): string | null {
  if (r.steps.length === 0) return null;
  const put = r.changed > 0 ? `, ${r.changed} put right` : '';
  return `Updated from format ${r.from} to ${r.to}${put}`;
}
